<template>
  <ValidationObserver ref="observer" v-slot="{ invalid }">
    <v-card class="mb-3" :class="{ completed: defect.isComplete }">
      <v-container class="py-0">
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="1">
                <ValidationProvider name="Line Number" rules="required|uniquelinenumber" v-slot="{ errors }">
                  <v-select label="#" v-model="defect.lineNumber"
                    :items="sourceSystem === 'OnRoad' ? onRoadNumberData : numberData" :error-messages="errors"
                    v-bind:disabled="!userIsFleetManager && editing"></v-select>
                </ValidationProvider>
              </v-col>
              <v-col cols="11">
                <ValidationProvider name="Description" rules="required" v-slot="{ errors }">
                  <v-textarea label="Describe the defect" v-model="defect.description" :error-messages="errors"
                    v-bind:disabled="!userIsFleetOrWorkshopManager" auto-grow rows="1"></v-textarea>
                </ValidationProvider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <ValidationProvider name="Scheduled Reason" :rules="(userIsFleetManager || !editing) ? 'required' : ''"
                  v-slot="{ errors }">
                  <v-select label="Scheduled Reason" v-model="defect.scheduledReasonId" item-value="id" item-text="name"
                    :items="reasons" :error-messages="errors"
                    v-bind:disabled="!userIsFleetManager && editing"></v-select>
                </ValidationProvider>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="9">
            <ValidationProvider name="Reason" rules="required" v-slot="{ errors }">
              <v-textarea label="Reason this defect was not completed" v-model="defect.reason" :error-messages="errors"
                v-bind:disabled="!userIsFleetOrWorkshopManager" auto-grow rows="1"></v-textarea>
            </ValidationProvider>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="3">
            <ValidationProvider name="Rating" rules="required" v-slot="{ errors }">
              <v-select label="Rating" v-model="defect.ratingId" item-value="id" item-text="name" :items="ratings"
                :error-messages="errors">
                <template slot="item" slot-scope="data">
                  <v-chip x-small v-if="data.item.auditOnly" class="mr-1 px-2">Audit</v-chip>
                  {{ data.item.name }}
                </template>
              </v-select>
            </ValidationProvider>
          </v-col>
          <v-col class="py-0" cols="3">
            <ValidationProvider name="Source" rules="required" v-slot="{ errors }">
              <v-select label="Source" v-model="defect.sourceId" item-value="id" item-text="name" :items="sources"
                :error-messages="errors" v-bind:disabled="!userIsFleetManager && editing"></v-select>
            </ValidationProvider>
          </v-col>
          <v-col class="py-0" cols="3">
            <ValidationProvider name="Category" rules="required" v-slot="{ errors }">
              <v-select label="Category" v-model="defect.categoryId" item-value="id" item-text="name"
                :items="categories" :error-messages="errors" v-bind:disabled="!userIsFleetOrWorkshopManager && editing"
                @change="categoryUpdated()"></v-select>
            </ValidationProvider>
          </v-col>
          <v-col class="py-0" cols="3">
            <v-menu v-model="scheduledDateDialog" :close-on-content-click="false" max-width="290"
              v-bind:disabled="expectedDateDisabled">
              <template v-slot:activator="{ on }">
                <ValidationProvider name="Expected Date"
                  :rules="'date_format:' + dateDisplayFormat + '|date_between:' + formattedReceivedDate + ',' + formattedMaxDate + ',true'"
                  v-slot="{ errors }">
                  <v-text-field label="Expected Date" v-model="formattedScheduledDate" readonly :error-messages="errors"
                    v-on="on" append-icon="event" :placeholder="'(' + defaultScheduledDate + ')'"
                    @click:append="scheduledDateDialog = true"
                    v-bind:disabled="expectedDateDisabled" :clearable="true"
                    @click:clear="defect.scheduledDate = null"></v-text-field>
                </ValidationProvider>
              </template>
              <v-date-picker no-title v-model="defect.scheduledDate" :min="defect.receivedDate" :max="maxDate(defect)"
                :disabled="expectedDateDisabled" @change="scheduledDateDialog = false"></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row v-if="defect.isComplete">
          <v-col>
            <v-text-field label="Completed by" :value="defect.completedBy" disabled hide-details></v-text-field>
          </v-col>
          <v-col>
            <v-text-field label="Completed on" :value="formattedCompletedOnDate" disabled hide-details></v-text-field>
          </v-col>
          <v-col>
            <v-text-field v-if='defect.completionHours >= 0' label="Completion hrs" :value="defect.completionHours"
              disabled hide-details></v-text-field>
            <v-text-field v-if='defect.completionKilometers >= 0' label="Completion kms"
              :value="defect.completionKilometers" disabled hide-details></v-text-field>
          </v-col>
          <v-col>
            <decimal-input label="Time taken" :value="defect.hoursWorked" :precision="2" :pad="false" disabled
              hide-details></decimal-input>
          </v-col>
        </v-row>
        <v-row v-if="defect.isComplete">
          <v-col>
            <v-textarea label="Completion Notes" :value="defect.notes" disabled auto-grow rows="1"
              hide-details></v-textarea>
          </v-col>
        </v-row>
        <v-row v-if="userIsFleetManager">
          <v-col class="py-0" cols="9">
            <ValidationProvider name="ReasonForExtension" v-slot="{ errors }">
              <v-textarea label="Reason for extension" v-model="defect.reasonForExtension" :error-messages="errors"
                v-bind:disabled="!editing || defect.isComplete"
                @change="defect.fleetExtended = defect.reasonForExtension.length > 0" auto-grow rows="1"></v-textarea>
            </ValidationProvider>
          </v-col>
          <v-col class="py-0" cols="3">
            <v-menu v-model="extendUntilDateDialog" :close-on-content-click="false" max-width="290"
              v-bind:disabled="!editing || defect.isComplete">
              <template v-slot:activator="{ on }">
                <ValidationProvider name="Expected Date" v-slot="{ errors }">
                  <v-text-field label="Extend Until Date" v-model="formattedExtensionDate" readonly
                    :error-messages="errors" v-on="on" append-icon="event" @click:append="extendUntilDateDialog = true"
                    v-bind:disabled="!editing || defect.isComplete" :clearable="true"
                    @click:clear="defect.extensionDate = null"></v-text-field>
                </ValidationProvider>
              </template>
              <v-date-picker no-title v-model="defect.extensionDate" :min="defect.receivedDate"
                @change="extendUntilDateDialog = false"></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row v-if="defect.photoBlobUri">
          <v-col cols="12" class="d-flex justify-center">
            <v-img :src="defect.photoBlobUri" max-width="300" max-height="240" contain
              class="rounded elevation-2 mx-auto" style="cursor: pointer;"
              @click="openPhotoModal(defect.photoBlobUri, defect.id)" />
          </v-col>
        </v-row>
      </v-container>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" class="mb-2 mr-5" v-if="editing && !defect.isComplete"
          :to="{ query: { defectId: defect.id, initiateFrom: 'schedule-defect' } }">Complete</v-btn>
        <v-btn v-if="defect.lineNumber && !otherLogVisible" color="tertiary" class="mb-2 mr-5"
          @click="toggleChildLog(defect)">
          Logs
          <v-icon small v-if="showChildLog">chevron_left</v-icon>
          <v-icon small v-else>chevron_right</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
    <photo-modal ref="photoModal" :photo-url="photoModalData.photoUrl" :defect-id="photoModalData.defectId"
      @photo-deleted="handlePhotoDeleted" />
  </ValidationObserver>
</template>

<script>
import moment from 'moment';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { DateFormat } from '@/plugins/date-filter';
import DefectHelper from '@/lib/DefectHelper';
import DecimalInput from '@/components/Decimal.vue';
import PhotoModal from '@/components/PhotoModal.vue';
import { Event } from '@/plugins/bus';

export default {
  props: {
    index: {
      required: true
    },
    defect: {
      required: true
    },
    sourceSystem: {
      required: true
    },
    sources: {
      required: true
    },
    categories: {
      required: true
    },
    ratings: {
      required: true
    },
    allDefects: {
      required: true
    },
    editing: {
      required: true
    },
    canEditExpectedDate: {
      type: Boolean,
      required: true
    },
    reasons: {
      required: true
    }
  },
  components: { DecimalInput, ValidationProvider, ValidationObserver, PhotoModal },
  data() {
    return {
      scheduledDateDialog: false,
      extendUntilDateDialog: false,
      numberData: [1, 2, 3, 4, 5, 6],
      onRoadNumberData: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
      today: moment().format(DateFormat.Canonical),
      todayFormatted: moment().format(DateFormat.DefaultDisplay),
      dateDisplayFormat: 'd MMMM yyyy',
      showChildLog: false,
      otherLogVisible: false,
      photoModalData: {
        photoUrl: '',
        defectId: null
      }
    };
  },
  methods: {
    handleNumberError(errorMsg) {
      this.errors.add({
        field: 'Number',
        msg: errorMsg
      });
    },
    maxDate(defect) {
      let mxDate = moment();
      const category = this.categories.filter(c => c.id === this.defect.categoryId)[0] || { defaultScheduleMonths: 3 };
      const categoryMaxDate = moment(defect.receivedDate).add(category.defaultScheduleMonths, 'M');
      if (moment(categoryMaxDate) > moment(mxDate)) {
        mxDate = moment(categoryMaxDate);
      }
      if (defect.extensionDate) {
        mxDate = moment(defect.extensionDate) > moment(mxDate) ? moment(defect.extensionDate) : moment(mxDate);
      }
      return mxDate.format(DateFormat.Canonical);
    },
    async categoryUpdated() {
      await this.$refs.observer.reset();
      await this.$refs.observer.validate();
    },
    openPhotoModal(photoUrl, defectId) {
      this.photoModalData.photoUrl = photoUrl;
      this.photoModalData.defectId = defectId;
      this.$refs.photoModal.showModal();
    },
    handlePhotoDeleted() {
      this.defect.photoBlobUri = '';
      this.photoModalData.photoUrl = '';
      this.$refs.photoModal.close();
    },
    async onParentLogVisibilityChanged(event) {
      // toggle my own defect visibility based on my parent's visibility
      this.otherLogVisible = event.visible;
    },
    async onCloseAllLogs() {
      this.showChildLog = false;
      this.otherLogVisible = false;
    },
    async onAnotherChildLogVisibilityChanged(event) {
      // toggle my own visibility based on my sibling's visibility
      if (event.defectId !== this.defect.id) {
        this.otherLogVisible = event.visible;
      }
    },
    toggleChildLog(defect) {
      this.showChildLog = !this.showChildLog;
      // let the parent know to show my log
      let event = { visible: this.showChildLog, defectId: defect.id, workSessions: defect.workSessions, assignees: defect.assignees, historicalAcceptedJobs: defect.historicalAcceptedJobs, defectNumber: defect.lineNumber };
      this.$bus.$emit(Event.ChildLogVisibilityChanged, event);
    },
  },
  computed: {
    expectedDateDisabled() {
      return !this.canEditExpectedDate || this.defect.isComplete;
    },
    formattedScheduledDate: {
      get() {
        return this.defect.scheduledDate ? moment(this.defect.scheduledDate).format(DateFormat.DefaultDisplay) : '';
      },
      set(newValue) {
        this.defect.scheduledDate = newValue;
      }
    },
    formattedExtensionDate: {
      get() {
        return this.defect.extensionDate ? moment(this.defect.extensionDate).format(DateFormat.DefaultDisplay) : '';
      },
      set(newValue) {
        this.defect.extensionDate = newValue;
      }
    },
    formattedReceivedDate() {
      return this.defect.receivedDate ? moment(this.defect.receivedDate).format(DateFormat.DefaultDisplay) : '';
    },
    formattedMaxDate() {
      let mxDate = moment();
      const category = this.categories.filter(c => c.id === this.defect.categoryId)[0] || { defaultScheduleMonths: 3 };
      const categoryMaxDate = moment(this.defect.receivedDate).add(category.defaultScheduleMonths, 'M');
      if (moment(categoryMaxDate) > moment(mxDate)) {
        mxDate = moment(categoryMaxDate);
      }
      if (this.defect.extensionDate) {
        mxDate = moment(this.defect.extensionDate) > moment(mxDate) ? moment(this.defect.extensionDate) : moment(mxDate);
      }
      return mxDate.format(DateFormat.DefaultDisplay);
    },
    formattedCompletedOnDate() {
      return this.defect.completedDate ? moment(this.defect.completedDate).format(DateFormat.DefaultDisplay) : '';
    },
    isAddCompletedButtonEnabled() {
      return (this.value.completedOnReceipt || 0) + this.completedDefects.length < this.value.receivedTotal;
    },
    defaultScheduledDate() {
      const d = DefectHelper.defaultDateFor(this.defect.categoryId, this.categories, new Date(this.defect.receivedDate));
      this.defect.defaultScheduledDate = d.format(DateFormat.Canonical);
      return d.format(DateFormat.DefaultDisplay);
    },
    deleteEdn() {
      this.$confirmCancel.show(`Delete Defect Notice?`, `<p>This will permanently delete this Defect Notice record</p>`, 'Delete it');
      var confirmHandler = confirmed => {
        this.$confirmCancel.$off('confirm-cancel', confirmHandler);
        if (!confirmed) {
          return;
        }
        this.$emit('delete-edn', { id: this.id, source: 'scheduled' });
      };
      this.$confirmCancel.$on('confirm-cancel', confirmHandler);
    },
  },
  watch: {
    'defect.ratingId': {
      async handler(newRatingId, oldRatingId) {
        this.$emit('ratingChanged', { newRatingId, oldRatingId });
      },
      immediate: true
    },
    showChildLog: {
      handler(newValue) {
        // let the parent know to show my log
        let event = { visible: newValue, defectId: this.defect.id, workSessions: this.defect.workSessions, assignees: this.defect.assignees, historicalAcceptedJobs: this.defect.historicalAcceptedJobs, defectNumber: this.defect.lineNumber };
        this.$bus.$emit(Event.ChildLogVisibilityChanged, event);
      }
    }
  },
  async created() {
    this.$bus.$on(Event.ParentLogVisibilityChanged, this.onParentLogVisibilityChanged);
    this.$bus.$on(Event.ChildLogVisibilityChanged, this.onAnotherChildLogVisibilityChanged);
    this.$bus.$on(Event.CloseAllLogs, this.onCloseAllLogs);
  },
  destroyed() {
    this.$bus.$off(Event.ParentLogVisibilityChanged, this.onParentLogVisibilityChanged);
    this.$bus.$off(Event.ChildLogVisibilityChanged, this.onAnotherChildLogVisibilityChanged);
    this.$bus.$off(Event.CloseAllLogs, this.onCloseAllLogs);
  }
};
</script>
